import React, {useState, useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import {Modal, Button, TextInput, Toggle} from '@/components/atoms';
import {MODAL_TYPE} from '@/utils/constants/globalStyle';
import {Icon} from '@pepsico-ds/ui';
import {REWARD_STATUS} from '@/utils/constants/rewardsConstants';
import {
  useModalPointsValidation,
  useModalQuotaValidation,
  useModalInventoryValidation
} from '@/containers/Rewards/validations';
import {
  getInitialFormData,
  getInitialErrors,
  initializeFormDataFromProduct,
  createPointsHandler,
  createInventoryHandler,
  createQuotaHandler,
  createFormValidator,
  createFormValidatorWithErrors,
  isInventoryDisabled,
  mergeProductDetailsWithOriginals
} from './productModalUtils';
import styles from './BulkProductDetailsModal.module.scss';

/**
 * Renders the title component for bulk product details modal
 * @param {Object} currentProduct - The current product being displayed
 * @param {number} currentIndex - The current index of the product (0-based)
 * @param {number} totalProducts - The total number of products
 * @returns {JSX.Element} The title component
 */
const renderBulkProductModalTitle = (
  currentProduct,
  currentIndex,
  totalProducts
) => {
  return (
    <div>
      <p className='text-2xl font-bold text-white'>
        {currentProduct.productName}
      </p>
      <p className='text-lg text-white'>
        ({currentIndex + 1} of {totalProducts})
      </p>
    </div>
  );
};

/**
 * BulkProductDetailsModal component for editing multiple product details
 * with navigation between products
 */
const BulkProductDetailsModal = ({
  showModal,
  onCloseModal,
  products,
  onApply,
  onCancel,
  rewardStatus
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [productsData, setProductsData] = useState([]);
  const [formData, setFormData] = useState(getInitialFormData());
  const [errors, setErrors] = useState(getInitialErrors());

  // Validation hooks using centralized validations
  const {onChange: validatePoints} = useModalPointsValidation();
  const {onChange: validateQuotaValue} = useModalQuotaValidation();
  const {onChange: validateInventory} = useModalInventoryValidation();

  const isActive = rewardStatus?.toUpperCase?.() === REWARD_STATUS.ACTIVE;

  const currentProductOriginals = useMemo(() => {
    const details = productsData[currentIndex]?.details || {};
    return {
      originalQuota: details.originalQuota ?? '',
      originalInventory: details.originalInventory ?? ''
    };
  }, [productsData, currentIndex]);

  const quotaOptions = useMemo(
    () => ({
      isActive,
      originalQuota: currentProductOriginals.originalQuota,
      originalInventory: currentProductOriginals.originalInventory
    }),
    [
      isActive,
      currentProductOriginals.originalQuota,
      currentProductOriginals.originalInventory
    ]
  );

  // Initialize products data when modal opens — keep originals on details
  useEffect(() => {
    if (showModal && products.length > 0) {
      const initialData = products.map((product) => ({
        ...product,
        details: {
          ...(product.details || {}),
          ...initializeFormDataFromProduct(product)
        }
      }));
      setProductsData(initialData);
      setCurrentIndex(0);
      updateFormData(initialData[0]);
      setErrors(getInitialErrors());
    }
  }, [showModal, products]);

  // Update form data when current index or products data changes
  useEffect(() => {
    if (productsData.length > 0 && currentIndex < productsData.length) {
      updateFormData(productsData[currentIndex]);
      setErrors(getInitialErrors());
    }
  }, [currentIndex, productsData]);

  // Update form data from product (editable fields only)
  const updateFormData = (product) => {
    if (product?.details) {
      setFormData(initializeFormDataFromProduct(product));
    }
  };

  // Save current form data to products data, preserving originals
  const saveCurrentFormData = () => {
    if (currentIndex < productsData.length) {
      const updatedProducts = [...productsData];
      updatedProducts[currentIndex] = {
        ...updatedProducts[currentIndex],
        details: mergeProductDetailsWithOriginals(
          updatedProducts[currentIndex].details,
          formData
        )
      };

      setProductsData(updatedProducts);
      return updatedProducts;
    }
  };

  // Create handlers using shared utilities — rebound when current product originals change
  const handlePointsChange = createPointsHandler(
    validatePoints,
    setErrors,
    setFormData
  );
  const handleInventoryChange = createInventoryHandler(
    validateInventory,
    setErrors,
    setFormData
  );
  const handleQuotaChange = createQuotaHandler(
    validateQuotaValue,
    setErrors,
    setFormData,
    formData,
    quotaOptions
  );

  // Create validators using shared utilities
  const isFormValid = createFormValidator(
    validatePoints,
    validateQuotaValue,
    formData,
    quotaOptions
  );
  const validateForm = createFormValidatorWithErrors(
    validatePoints,
    validateQuotaValue,
    formData,
    setErrors,
    quotaOptions
  );

  // Handle previous button click
  const handlePrevious = () => {
    if (currentIndex > 0) {
      saveCurrentFormData();
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Handle next button click
  const handleNext = () => {
    if (validateForm() && currentIndex < productsData.length - 1) {
      saveCurrentFormData();
      setCurrentIndex(currentIndex + 1);
    }
  };

  // Handle apply button click
  const handleApply = () => {
    if (validateForm()) {
      let updatedProductsData = saveCurrentFormData();
      onApply(updatedProductsData);
      onCloseModal();
    }
  };

  // Handle cancel button click
  const handleCancel = () => {
    onCancel();
    onCloseModal();
  };

  if (!showModal || productsData.length === 0) return null;

  const currentProduct = productsData[currentIndex];
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === productsData.length - 1;

  return (
    <Modal
      type={MODAL_TYPE.SMALL}
      hideCloseButton={false}
      showModal={showModal}
      title={renderBulkProductModalTitle(
        currentProduct,
        currentIndex,
        productsData.length
      )}
      className={styles.bulkProductDetailsModalWrapper}
      isDark={true}
      showSecondaryButton={false}
      showPrimaryButton={false}
      onCloseModal={handleCancel}
      content={
        <div
          className={`flex flex-col gap-6 pb-4 pt-4 ${styles.bulkProductDetailsModal}`}>
          {/* Points Field */}
          <TextInput
            label='Points*'
            value={formData.points}
            onChange={handlePointsChange}
            placeholderText='Add Points'
            error={errors.points}
            isDark={true}
            type='text'
          />

          {/* Quota Section */}
          <div className='flex flex-col gap-2'>
            <p className='flex items-center gap-2 text-sm font-medium text-white'>
              Quota*{' '}
              <span className='text-xs text-white'>
                <Icon icon='info' size='small' />
              </span>
            </p>

            {/* Unlimited Toggle */}
            <Toggle
              value={formData.quota.unlimited}
              updateValue={(value) => handleQuotaChange('unlimited', value)}
              uiSchema={{
                label: 'Unlimited Quota',
                size: 'small',
                isDark: true,
                variant: 'horizontal',
                showCheckedIcon: false
              }}
              disabled={false}
            />

            {/* Quota Value Input - Only show when unlimited is false */}
            {!formData.quota.unlimited && (
              <TextInput
                label='Quota Value*'
                value={formData.quota.value}
                onChange={(value) => handleQuotaChange('value', value)}
                placeholderText='Add Quota Value'
                error={errors.quota}
                isDark={true}
                type='text'
              />
            )}
          </div>

          {/* Inventory Field */}
          <TextInput
            label='Inventory'
            value={formData.inventory}
            onChange={handleInventoryChange}
            error={errors.inventory}
            isDark={true}
            type='text'
            disabled={isInventoryDisabled()}
          />

          {/* Navigation and Action Buttons */}
          <div className='flex items-center justify-end pt-4'>
            <div className='flex gap-4'>
              <Button
                handleButton={handlePrevious}
                uiSchema={{
                  variant: 'tertiary',
                  size: 'medium',
                  disabled: isFirst
                }}>
                Previous
              </Button>
              <Button
                handleButton={isLast ? handleApply : handleNext}
                uiSchema={{
                  variant: 'primary',
                  size: 'medium',
                  disabled: !isFormValid()
                }}>
                {isLast ? 'Apply' : 'Next'}
              </Button>
            </div>
          </div>
        </div>
      }
    />
  );
};

BulkProductDetailsModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      productId: PropTypes.string,
      productName: PropTypes.string,
      details: PropTypes.shape({
        points: PropTypes.string,
        quota: PropTypes.shape({
          unlimited: PropTypes.bool,
          value: PropTypes.string
        }),
        inventory: PropTypes.string,
        originalQuota: PropTypes.string,
        originalInventory: PropTypes.string
      })
    })
  ),
  onApply: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  rewardStatus: PropTypes.string
};

export default BulkProductDetailsModal;
